import { Skeleton } from '@/components/ui/skeleton';
import { DeltaBadge } from './DeltaBadge';
import { EmptyState } from './EmptyState';
import type { FunnelStage } from '@/hooks/useAggregatorFunnel';
import { formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';

interface Props {
  stages: FunnelStage[];
  loading?: boolean;
}

export function FunnelChart({ stages, loading }: Props) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }
  const top = stages[0]?.metric.current ?? 0;
  if (top === 0) {
    return (
      <EmptyState
        title="No funnel data"
        description="No impressions logged for this period — check provider filter or sync runs."
      />
    );
  }
  return (
    <div className="space-y-2">
      {stages.map((s, i) => {
        const width = top > 0 ? Math.max(8, (s.metric.current / top) * 100) : 0;
        const tone =
          s.key === 'impressions'
            ? 'bg-sky-500/80'
            : s.key === 'menu_opens'
              ? 'bg-emerald-500/80'
              : s.key === 'carts_built'
                ? 'bg-amber-500/80'
                : 'bg-violet-500/80';
        return (
          <div key={s.key} className="space-y-1">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="font-medium">{s.label}</span>
              <span className="flex items-center gap-2">
                <span className="font-mono tabular-nums">
                  {formatNumberCompact(s.metric.current)}
                </span>
                <DeltaBadge
                  deltaPct={s.metric.deltaPct}
                  comparable={s.metric.comparable}
                />
              </span>
            </div>
            <div className="relative h-7 w-full rounded-md bg-muted/30">
              <div
                className={cn('h-full rounded-md transition-all', tone)}
                style={{ width: `${width}%` }}
              />
              {i > 0 && s.rateFromPrevious !== undefined && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-2xs text-muted-foreground">
                  {s.rateFromPrevious.toFixed(1)}% conv
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
